import { css, html, nothing } from 'lit';
import { api } from '../../lib/api.js';
import { BaseComponent } from '../../lib/base-component.js';
import { briefHTML } from '../../lib/brief.js';
import { BRIEF_STYLES } from '../../lib/brief-styles.js';
import { createCommissionSSE } from '../../lib/commission-sse.js';
import { loadConfig } from '../../lib/config.js';
import {
    downloadMarkdown,
    formatCents,
    phaseDisplayName,
    sizeLabel,
    versionLabel,
} from '../../lib/format.js';
import {
    maxSubmissionsForPhase,
    phaseReadyForReview,
    phaseSubmitBlocker,
    submissionProcessing,
} from '../../lib/phase-submissions.js';
import { router } from '../../lib/router.js';
import { commissionTemplateLinks } from '../../lib/templates.js';
import '../shared/image-lightbox.js';
import '../shared/media-thumb.js';
import '../shared/upload-progress.js';
import './sow-view.js';
import './tile-brush-previews.js';
// Side-effect import: registers <miniature-grid-preview>, shared with the
// miniature detail view so the artist sees their art placed exactly as staff do.
import '../miniatures/miniature-grid-preview.js';

const STATUS_COLORS = {
    assigned: 'bg-info',
    completed: 'bg-success',
};

class AssignmentsView extends BaseComponent {
    static properties = {
        _commissions: { type: Array, state: true },
        _loading: { type: Boolean, state: true },
        _error: { type: String, state: true },
        _view: { type: String, state: true },
        _selected: { type: Object, state: true },
        _modal: { type: String, state: true },
        _lightboxSrc: { type: String, state: true },
        _phases: { type: Array, state: true },
        _submitting: { type: Boolean, state: true },
        _submitNote: { type: String, state: true },
        _detailTab: { type: String, state: true },
        _config: { type: Object, state: true },
        _miniatureSizes: { type: Object, state: true },
        _miniatureCellPx: { type: Number, state: true },
        // phaseId -> { layers } | { error }, populated after a tile-brush
        // upload so the phase can render a live render preview.
        _tileBrushPreview: { type: Object, state: true },
    };

    static styles = [
        BRIEF_STYLES,
        css`
        :host { display: block; }
        .page-header { margin-bottom: 0.5rem; }
        .page-header h4 { margin: 0; font-size: 1.5rem; font-weight: 600; }
        .page-subtitle { color: #666; font-size: 0.9rem; margin-bottom: 1.5rem; }
        table { font-size: 0.9rem; }
        .back-link {
            display: inline-flex; align-items: center; gap: 0.25rem;
            color: #666; text-decoration: none; font-size: 0.9rem; margin-bottom: 1rem;
        }
        .back-link:hover { color: #333; }
        .detail-header { display: flex; align-items: center; justify-content: space-between; margin-bottom: 1.5rem; }
        .detail-header-left { display: flex; align-items: center; gap: 1rem; }
        .detail-header h4 { margin: 0; font-size: 1.5rem; font-weight: 600; }
        .detail-section { border: 1px solid #eee; border-radius: 8px; padding: 1.25rem; margin-bottom: 1.25rem; }
        .detail-section h6 { font-weight: 600; margin-bottom: 1rem; }
        .brief-text { font-size: 0.9rem; }
        .spec-text { white-space: pre-wrap; font-size: 0.9rem; line-height: 1.6; }
        .detail-layout { display: flex; gap: 2rem; }
        .detail-main { flex: 1; min-width: 0; }
        .detail-sidebar { width: 280px; flex-shrink: 0; }
        .ref-images-grid { display: flex; flex-wrap: wrap; gap: 0.75rem; }
        .ref-thumb {
            width: 120px; border: 2px solid #dee2e6; border-radius: 6px; overflow: hidden; cursor: pointer;
        }
        .ref-thumb img { width: 100%; aspect-ratio: 1; object-fit: contain; background: #f8f9fa; transition: opacity 0.15s; }
        .ref-thumb img:hover { opacity: 0.8; }
        .ref-thumb-info { padding: 0.25rem 0.5rem; font-size: 0.7rem; color: #666; }
        .spec-link {
            display: inline-flex; align-items: center; gap: 0.5rem;
            padding: 0.5rem 0.75rem; border: 1px solid #dee2e6; border-radius: 0.375rem;
            text-decoration: none; color: #333; font-size: 0.9rem;
        }
        .spec-link:hover { background: #f8f9fa; border-color: #adb5bd; }
        .spec-link i { color: #666; }
        .modal-overlay {
            position: fixed; inset: 0; background: rgba(0,0,0,0.5);
            display: flex; align-items: center; justify-content: center; z-index: 9998;
        }
        .modal-panel {
            background: white; border-radius: 8px; width: 90vw; max-width: 700px; max-height: 85vh;
            display: flex; flex-direction: column; box-shadow: 0 1rem 3rem rgba(0,0,0,0.2);
        }
        .modal-header {
            display: flex; align-items: center; justify-content: space-between;
            padding: 1rem 1.25rem; border-bottom: 1px solid #dee2e6;
        }
        .modal-header h5 { margin: 0; font-weight: 600; }
        .modal-body { padding: 1.25rem; overflow-y: auto; flex: 1; }
        .modal-footer {
            display: flex; justify-content: flex-end; gap: 0.5rem;
            padding: 0.75rem 1.25rem; border-top: 1px solid #dee2e6;
        }
        .status-badge { font-size: 0.75rem; }
        .phase-stepper { margin-bottom: 1.5rem; }
        .phase-step {
            border: 1px solid #eee; border-radius: 8px; padding: 1rem 1.25rem; margin-bottom: 0.75rem;
        }
        .phase-step.active { border-color: #0d6efd; background: #f0f6ff; }
        .phase-step.accepted { border-color: #198754; background: #f0faf4; }
        .phase-step.changes-requested { border-color: #ffc107; background: #fffdf0; }
        .phase-step-header {
            display: flex; align-items: center; justify-content: space-between; margin-bottom: 0.5rem;
        }
        .phase-step-title { font-weight: 600; font-size: 0.95rem; }
        .submission-grid { display: flex; flex-wrap: wrap; gap: 0.75rem; margin-top: 0.75rem; }
        .submission-thumb {
            width: 120px; border: 2px solid #dee2e6; border-radius: 6px; overflow: hidden; cursor: pointer;
        }
        .submission-thumb.approved { border-color: #198754; }
        .submission-thumb img { width: 100%; aspect-ratio: 1; object-fit: contain; background: #f8f9fa; transition: opacity 0.15s; cursor: pointer; }
        .submission-thumb img:hover { opacity: 0.8; }
        .submission-thumb-info { padding: 0.25rem 0.5rem; font-size: 0.7rem; color: #666; }
        .submission-processing { padding: 0 0.5rem 0.35rem; font-size: 0.7rem; color: #666; }
        .submission-processing.failed { color: #b02a37; }
        .submit-blocker { font-size: 0.8rem; color: #666; margin-left: 0.5rem; }
        .review-note { background: #fff3cd; border-radius: 4px; padding: 0.5rem 0.75rem; font-size: 0.85rem; margin-top: 0.5rem; }
    `,
    ];

    constructor() {
        super();
        this._commissions = [];
        this._loading = true;
        this._error = null;
        this._view = 'list';
        this._selected = null;
        this._miniatureSizes = {};
        this._miniatureCellPx = 0;
        this._modal = null;
        this._lightboxSrc = '';
        this._phases = [];
        this._submitting = false;
        this._submitNote = '';
        this._detailTab = 'submissions';
        this._tileBrushPreview = {};
    }

    async connectedCallback() {
        super.connectedCallback();
        this._claimInvitation();
        this._unsubscribeRouter = router.subscribe(() => this._syncFromRoute());
        this._syncFromRoute();
        loadConfig()
            .then((cfg) => {
                this._config = cfg;
                this._miniatureSizes = cfg.miniatureSizes || {};
                this._miniatureCellPx = cfg.miniatureCellPx || 0;
            })
            .catch(() => {});
    }

    // This is where an invited freelancer lands: d3-auth sends them to the
    // registered redirect_uri and appends ?invitation={id}. That id is the join
    // key back to the contract record staff raised for them, and it is the only
    // place it appears — nothing else carries it to the API.
    //
    // Deliberately fire-and-forget. Linking is a background reconciliation, not
    // what the invitee came here for, and the server still links by invited
    // email on every request as a fallback. A failure is logged and nothing
    // else; blocking the assignments list on it would trade the page they asked
    // for against bookkeeping.
    async _claimInvitation() {
        const invitationId = new URLSearchParams(window.location.search).get('invitation');
        if (!invitationId) return;

        // Stripped first, so a reload or a shared URL cannot replay it and a
        // failed call is not retried forever by a refresh.
        const url = new URL(window.location.href);
        url.searchParams.delete('invitation');
        window.history.replaceState({}, '', url);

        try {
            await api.post('/api/freelancers/contracts/claim', { invitation_id: invitationId });
        } catch (err) {
            console.error('Failed to claim the invitation', err);
        }
    }

    disconnectedCallback() {
        super.disconnectedCallback();
        if (this._unsubscribeRouter) this._unsubscribeRouter();
        this._disconnectSSE();
    }

    async _syncFromRoute() {
        const path = router.getPath();
        if (path.match(/^\/assignments\/[^/]+$/) && router.params.id) {
            await this._loadDetail(router.params.id);
            this._view = 'detail';
        } else {
            this._disconnectSSE();
            this._view = 'list';
            this._selected = null;
            this._loadAssignments();
        }
    }

    async _loadAssignments() {
        this._loading = true;
        this._error = null;
        try {
            const data = await api.get('/api/commissions/assignments');
            this._commissions = data.commissions || [];
        } catch (err) {
            this._error = err.message;
        } finally {
            this._loading = false;
        }
    }

    async _loadDetail(id) {
        this._error = null;
        try {
            this._selected = await api.get(`/api/commissions/assignments/${id}`);
            // An offer has no phases and no submissions yet — the only thing to
            // do with it is sign the SOW, which lives on the Details tab. Landing
            // on Submissions would show an empty screen and hide the one action.
            if (this._selected.offered_to) {
                this._detailTab = 'details';
            }
            await this._loadPhases(id);
            this._connectSSE(id);
        } catch (err) {
            this._error = err.message;
        }
    }

    async _loadPhases(id) {
        try {
            const data = await api.get(`/api/commissions/assignments/${id}/phases`);
            this._phases = data.phases || [];
        } catch {
            this._phases = [];
        }
    }

    _connectSSE(commissionId) {
        this._disconnectSSE();
        this._sse = createCommissionSSE(commissionId, (event) => this._handleSSEEvent(event));
        this._sse.connect();
    }

    _disconnectSSE() {
        if (this._sse) {
            this._sse.disconnect();
            this._sse = null;
        }
    }

    _handleSSEEvent(_event) {
        if (this._selected) {
            this._loadPhases(this._selected.id);
        }
    }

    // Fetch the per-component preview tiles for a just-uploaded tile-brush PSD
    // and stash them (or an error) by phase so _renderPhase can show a live
    // render. Best-effort: a preview failure never blocks the submission.
    async _fetchTileBrushPreview(phaseId, uploadId) {
        try {
            const resp = await api.post(
                `/api/commissions/assignments/${this._selected.id}/preview-extraction`,
                { upload_id: uploadId },
            );
            this._tileBrushPreview = {
                ...this._tileBrushPreview,
                [phaseId]: { layers: resp.layers || [] },
            };
        } catch (err) {
            this._tileBrushPreview = {
                ...this._tileBrushPreview,
                [phaseId]: { error: err?.message || 'Preview failed' },
            };
        }
    }

    // The tile-brush preview for a phase: the live one fetched right after an
    // upload, else the layers the server extracted and stored, so the preview
    // survives a reload and revisiting the assignment (#40, #90).
    _tileBrushPreviewFor(phase) {
        if (this._tileBrushPreview[phase.id]) return this._tileBrushPreview[phase.id];
        const layers = phase.submissions?.[0]?.layers;
        return layers?.length ? { layers } : null;
    }

    // A miniature submission, or an image submitted as a PSD, is flattened
    // server-side into a "rendered" layer; return that image so we show the art,
    // not a PSD placeholder a browser can't draw.
    _renderedLayerUrl(s) {
        const type = this._selected?.asset_type;
        if ((type === 'miniature' || type === 'image') && s?.layers?.length) {
            return s.layers[0].file_url;
        }
        return null;
    }

    // Where a PSD submission's processing stands, shown under its thumbnail so a
    // PSD is never silently waiting or silently broken.
    _renderSubmissionProcessing(s) {
        const { state, message } = submissionProcessing(s);
        if (state === 'processing') {
            return html`<div class="submission-processing d-flex align-items-center gap-1">
                <span class="spinner-border spinner-border-sm" role="status"></span>${message}
            </div>`;
        }
        if (state === 'failed') {
            return html`<div class="submission-processing failed">${message}</div>`;
        }
        return nothing;
    }

    // The artist's own grid preview of what they submitted: the approved
    // submission if there is one, otherwise the newest. Their art may overflow the
    // cells the miniature occupies, and this is where they can see how far.
    _renderMiniatureGridPreview(phase) {
        if (this._selected?.asset_type !== 'miniature') {
            return nothing;
        }
        const submissions = phase.submissions || [];
        const submission =
            submissions.find((s) => s.id === phase.approved_submission_id) || submissions[0];
        const src = this._renderedLayerUrl(submission);
        if (!src) {
            // A PSD's preview is flattened on the server's job queue after upload;
            // the view refetches when it lands (submission:extracted).
            return submission?.mime_type === 'image/vnd.adobe.photoshop'
                ? html`<div class="mt-3 text-muted small d-flex align-items-center gap-2">
                      <span class="spinner-border spinner-border-sm" role="status"></span>
                      The miniature preview appears here once the PSD has been processed.
                  </div>`
                : nothing;
        }
        const spec = this._miniatureSizes?.[this._selected?.miniature_size];
        return html`
            <div class="mt-3">
                <miniature-grid-preview
                    src="${src}"
                    footprint-cells="${spec?.footprintCells ?? 0}"
                    canvas-cells="${spec?.canvasCells ?? 0}"
                    cell-px="${this._miniatureCellPx}"
                    cell-size="64"></miniature-grid-preview>
                <div class="text-muted small mt-1">
                    ${this._submissionLabel(this._selected, phase, submission)} on the grid it occupies — shaded square is the footprint.
                </div>
            </div>
        `;
    }

    async _handlePhaseUploadComplete(phaseId, e) {
        const { upload_id } = e.detail;
        const uploader = e.target;
        this._error = null;
        try {
            const form = new FormData();
            form.append('upload_id', upload_id);
            await api.upload(
                `/api/commissions/assignments/${this._selected.id}/phases/${phaseId}/submissions`,
                form,
            );
            await this._loadPhases(this._selected.id);
            if (uploader?.reset) uploader.reset();
            // Tile-brush: render a live preview of how this upload will render
            // so the freelancer can eyeball it before submitting for review.
            if (this._selected?.asset_type === 'mapmaking-tile-brush') {
                this._fetchTileBrushPreview(phaseId, upload_id);
            }
        } catch (err) {
            // Surface the rejection inline in the drop-zone instead of in the
            // top-level alert. The file already finished uploading, so without
            // this the uploader would be stuck in its "complete" state showing
            // a preview + delete button alongside an alert at the top of the
            // page — confusing, since the user's next move is just to drop a
            // different file.
            if (uploader?.showRejection) {
                uploader.showRejection(err.message);
            } else {
                this._error = err.message;
            }
        }
    }

    async _removeSubmission(phaseId, submissionId) {
        this._error = null;
        try {
            await api.delete(
                `/api/commissions/assignments/${this._selected.id}/phases/${phaseId}/submissions/${submissionId}`,
            );
            await this._loadPhases(this._selected.id);
        } catch (err) {
            this._error = err.message;
        }
    }

    /**
     * Click handler for submission thumbs. Renderable images go to the
     * lightbox; PSDs (and anything else the browser can't render inline)
     * trigger a download instead.
     * @param {{file_url: string, file_name?: string, mime_type?: string}} s
     */
    _handleSubmissionClick(s) {
        // A PSD with a rendered preview opens the preview, as the review page does;
        // one without (a tile brush, or still processing) downloads the PSD.
        const rendered = this._renderedLayerUrl(s);
        if (rendered) {
            this._lightboxSrc = rendered;
            return;
        }
        const isPSD =
            s.mime_type === 'image/vnd.adobe.photoshop' || /\.psd($|\?)/i.test(s.file_url || '');
        if (isPSD) {
            const a = document.createElement('a');
            a.href = s.file_url;
            a.download = s.file_name || '';
            document.body.appendChild(a);
            a.click();
            a.remove();
            return;
        }
        this._lightboxSrc = s.file_url;
    }

    async _submitForReview(phaseId) {
        this._submitting = true;
        this._error = null;
        try {
            await api.post(
                `/api/commissions/assignments/${this._selected.id}/phases/${phaseId}/submit`,
                {
                    note: this._submitNote,
                },
            );
            this._submitNote = '';
            await this._loadPhases(this._selected.id);
        } catch (err) {
            this._error = err.message;
        } finally {
            this._submitting = false;
        }
    }

    _renderReferenceImages(images) {
        if (!images || images.length === 0) return nothing;
        return html`
            <div class="detail-sidebar">
                <h6 style="font-weight: 600; font-size: 0.85rem; margin-bottom: 0.5rem;">Reference Images</h6>
                <div class="ref-images-grid">
                    ${images.map(
                        (img) => html`
                        <div class="ref-thumb">
                            <media-thumb src="${img.file_url}" alt="${versionLabel(img.version)}"
                                 @media-click="${() => (this._lightboxSrc = img.file_url)}"></media-thumb>
                            <div class="ref-thumb-info">${versionLabel(img.version)}</div>
                        </div>
                    `,
                    )}
                </div>
            </div>
        `;
    }

    _renderList() {
        return html`
            <div class="page-header">
                <h4>My Assignments</h4>
            </div>
            <p class="page-subtitle">Commissions assigned to you, and offers waiting on your signature.</p>

            ${
                this._loading
                    ? html`
                <div class="text-center py-4"><div class="spinner-border spinner-border-sm text-primary" role="status"></div></div>
            `
                    : html`
                <table class="table table-hover">
                    <thead>
                        <tr>
                            <th>Slug</th>
                            <th>Phase</th>
                            <th>Rate</th>
                        </tr>
                    </thead>
                    <tbody>
                        ${this._commissions.map(
                            (c) => html`
                            <tr style="cursor: pointer;" @click="${() => router.navigate(`/assignments/${c.id}`)}">
                                <td><span style="color: #0d6efd; font-weight: 500;">${c.slug}</span></td>
                                <td>
                                    ${
                                        c.offered_to
                                            ? html`<span class="badge bg-warning text-dark status-badge">Awaiting your signature</span>`
                                            : c.current_phase_status === 'completed'
                                              ? html`<span class="badge bg-warning text-dark status-badge">Payment Pending</span>`
                                              : c.current_phase
                                                ? html`
                                        <span>${phaseDisplayName(c.current_phase)}</span>
                                        ${c.current_phase_status === 'submitted' ? html` <span class="badge bg-info status-badge">pending review</span>` : ''}
                                        ${c.current_phase_status === 'changes-requested' ? html` <span class="badge bg-warning text-dark status-badge">changes requested</span>` : ''}
                                    `
                                                : html`<span class="text-muted">-</span>`
                                    }
                                </td>
                                <td>${formatCents(c.rate_cents)}</td>
                            </tr>
                        `,
                        )}
                    </tbody>
                </table>

                ${this._commissions.length === 0 ? html`<p class="text-muted text-center py-3">No assignments yet.</p>` : nothing}
            `
            }
        `;
    }

    _renderDetail() {
        const c = this._selected;
        if (!c) return nothing;

        return html`
            <a class="back-link" href="/assignments">
                <i class="bi bi-arrow-left"></i> Back to Assignments
            </a>

            <div class="detail-header">
                <div class="detail-header-left">
                    <h4>${c.slug}</h4>
                    <span class="badge ${STATUS_COLORS[c.status] || 'bg-secondary'}">${c.status}</span>
                    ${
                        c.offered_to
                            ? html`<span class="badge bg-warning text-dark">awaiting your signature</span>`
                            : nothing
                    }
                </div>
            </div>

            ${
                c.offered_to
                    ? html`<div class="alert alert-warning">
                This commission has been offered to you. Review the Statement of Work below and accept it
                to take on the work — the commission is assigned to you once you sign.
            </div>`
                    : nothing
            }

            <ul class="nav nav-tabs mb-4">
                <li class="nav-item">
                    <a class="nav-link ${this._detailTab === 'submissions' ? 'active' : ''}" href="#"
                       @click="${(e) => {
                           e.preventDefault();
                           this._detailTab = 'submissions';
                       }}">Submissions</a>
                </li>
                <li class="nav-item">
                    <a class="nav-link ${this._detailTab === 'details' ? 'active' : ''}" href="#"
                       @click="${(e) => {
                           e.preventDefault();
                           this._detailTab = 'details';
                       }}">Details</a>
                </li>
            </ul>

            ${
                this._detailTab === 'submissions'
                    ? html`
                <p class="text-muted" style="font-size: 0.9rem; margin-bottom: 1.5rem;">
                    Work through each phase in order. Upload your submissions, then click <strong>Submit for Review</strong> when ready.
                    Your art director will review and either accept or request changes before you move to the next phase.
                </p>
                ${
                    this._phases.length > 0
                        ? html`
                    <div class="phase-stepper">
                        ${this._phases.map((p) => this._renderPhase(p))}
                    </div>
                `
                        : html`<p class="text-muted">No phases available.</p>`
                }
            `
                    : ''
            }

            ${
                this._detailTab === 'details'
                    ? html`
                <div class="detail-layout">
                    <div class="detail-main">
                        <div class="detail-section">
                            <h6>Statement of Work</h6>
                            ${
                                c.staff_assignment
                                    ? html`<p class="text-muted mb-0">Staff assignment, no SOW.</p>`
                                    : html`<sow-view
                                          commission-id="${c.id}"
                                          @sow-signed="${() => this._loadDetail(c.id)}"
                                      ></sow-view>`
                            }
                        </div>

                        <div class="detail-section">
                            <div class="row mb-2">
                                <div class="col-6">
                                    <span class="text-muted small">Type</span>
                                    <div class="fw-medium">${c.asset_type}</div>
                                </div>
                                <div class="col-6">
                                    <span class="text-muted small">Rate</span>
                                    <div class="fw-medium">${formatCents(c.rate_cents)}</div>
                                </div>
                            </div>
                            ${
                                (c.asset_type === 'miniature' || c.asset_type === 'tactical-map') &&
                                c.miniature_size
                                    ? html`
                                <div class="row">
                                    <div class="col-6">
                                        <span class="text-muted small">Size</span>
                                        <div class="fw-medium">${sizeLabel(c.asset_type, c.miniature_size)}</div>
                                    </div>
                                    ${
                                        c.asset_type === 'miniature'
                                            ? html`
                                        <div class="col-6">
                                            <span class="text-muted small">Miniature Type</span>
                                            <div class="fw-medium">${c.miniature_type ? (c.miniature_type === 'pc' ? 'PC' : c.miniature_type === 'monster' ? 'Monster' : c.miniature_type) : html`<span class="text-muted">Not set</span>`}</div>
                                        </div>
                                    `
                                            : nothing
                                    }
                                </div>
                            `
                                    : nothing
                            }
                        </div>

                        <div class="detail-section">
                            <h6>Brief</h6>
                            <div class="brief-text brief-md" .innerHTML="${briefHTML(c.brief)}"></div>
                        </div>

                        <div class="detail-section">
                            <h6>Reference Documents</h6>
                            <div class="d-flex gap-3">
                                ${
                                    c.style_guide
                                        ? html`
                                    <a class="spec-link" href="#" @click="${(e) => {
                                        e.preventDefault();
                                        this._modal = 'style-guide';
                                    }}">
                                        <i class="bi bi-palette"></i> Style Guide
                                    </a>
                                `
                                        : nothing
                                }
                                ${
                                    c.technical_spec
                                        ? html`
                                    <a class="spec-link" href="#" @click="${(e) => {
                                        e.preventDefault();
                                        this._modal = 'technical-spec';
                                    }}">
                                        <i class="bi bi-rulers"></i> Technical Spec
                                    </a>
                                `
                                        : nothing
                                }
                                ${commissionTemplateLinks(c, this._config).map(
                                    (link) => html`
                                        <a class="spec-link" href="${link.url}" download>
                                            <i class="bi bi-file-earmark-arrow-down"></i> ${link.label}
                                        </a>
                                    `,
                                )}
                            </div>
                        </div>
                    </div>

                    ${this._renderReferenceImages(c.reference_images)}
                </div>
            `
                    : ''
            }

            <image-lightbox .src="${this._lightboxSrc}" @close="${() => (this._lightboxSrc = '')}"></image-lightbox>
        `;
    }

    _phaseDescription(type) {
        switch (type) {
            case 'composition':
                return 'Establish the overall layout, framing, and placement of key elements. Define the scene composition, focal points, and spatial relationships. All structural and layout changes must be resolved at this stage.';
            case 'line-art':
                return 'Create clean, detailed linework over the approved composition at final resolution. Establish all detail, form, and structure. Line quality and completeness should be production-ready.';
            case 'sketch':
                return 'Submit a clean line sketch on a transparent background at the assigned canvas size. Establish the pose, proportions, silhouette, and placement of all weapons and equipment. The miniature must read clearly at thumbnail size. All structural changes must be resolved at this stage.';
            case 'color-rough':
                return 'Apply flat color fills over the approved sketch. No rendering or shading \u2014 just broad colors that establish the palette, distinguish materials (metal, leather, cloth, skin, fur, etc.), and show the overall value structure. All color direction changes must be resolved at this stage.';
            case 'final-render':
                return 'Fully paint the miniature with shading, texture, and material detail. Submit without drop shadow. Edges should be clean with no stray pixels or artifacts. Rendering quality and finish level should be consistent across the entire miniature. Only minor adjustments at this stage.';
            case 'delivery':
                return 'Apply the drop shadow per the project shadow standard provided in your brief. Verify transparency is clean, canvas dimensions are correct, and the file name follows the naming convention. This is a checklist pass, not a creative stage.';
            default:
                return '';
        }
    }

    _phaseStatusBadge(status) {
        const colors = {
            pending: 'bg-secondary',
            'in-progress': 'bg-primary',
            submitted: 'bg-info',
            accepted: 'bg-success',
            'changes-requested': 'bg-warning text-dark',
        };
        return html`<span class="badge status-badge ${colors[status] || 'bg-secondary'}">${status}</span>`;
    }

    /**
     * Per-asset-type / per-phase upload accept list. Mirrors the server-side
     * constraints in internal/assets/commissions/submission_validation.go —
     * keep them in sync. Returning a tighter accept here saves the artist
     * the round trip when an obviously wrong format is dropped.
     * @param {object} commission
     * @param {object} _phase
     * @returns {string}
     */
    _acceptForPhase(commission, _phase) {
        if (commission?.asset_type === 'miniature') {
            // Miniatures are PSD-first: every phase is a PSD, flattened server-side
            // into the rendered miniature image for review and delivery.
            return '.psd';
        }
        if (commission?.asset_type === 'mapmaking-tile-brush') {
            // Every tile-brush phase submission is a PSD — same upload
            // contract from sketch through delivery. Server-side strictness
            // (layer names + sizes) is enforced in the async extraction
            // worker and the SubmitForReview gate.
            return '.psd';
        }
        if (commission?.asset_type === 'image') {
            // An image phase takes a layered PSD (flattened server-side into the
            // image it serves, as a miniature's is) or a ready PNG, WebP or JPEG.
            return 'image/*,.psd';
        }
        return 'image/*,video/webm';
    }

    /**
     * Display label for a single submission. Every miniature phase is one PSD,
     * labeled "Miniature"; every tile-brush phase is one PSD, labeled "PSD".
     * Everything else falls back to the version letter (A, B, …).
     * @param {object} commission
     * @param {object} _phase
     * @param {object} submission
     * @returns {string}
     */
    _submissionLabel(commission, _phase, submission) {
        if (commission?.asset_type === 'miniature') return 'Miniature';
        if (commission?.asset_type === 'mapmaking-tile-brush') return 'PSD';
        return versionLabel(submission.version);
    }

    /**
     * Whether the phase has what it needs to be submitted for review (see
     * phaseReadyForReview, which mirrors the server's check).
     * @param {object} commission
     * @param {object} phase
     * @returns {boolean}
     */
    _phaseReadyForReview(commission, phase) {
        return phaseReadyForReview(commission?.asset_type, phase?.phase_type, phase?.submissions);
    }

    /**
     * Maximum number of submissions allowed for a phase. Once reached,
     * the drop zone is hidden — the artist must delete an existing
     * submission to replace it.
     * @param {object} commission
     * @param {object} _phase
     * @returns {number}
     */
    _maxSubmissionsForPhase(commission, _phase) {
        return maxSubmissionsForPhase(commission?.asset_type);
    }

    /**
     * Drop-zone label for a given phase. Calls out the size constraint for
     * miniature sketches so the artist knows the requirement before they upload.
     * @param {object} commission
     * @param {object} _phase
     * @returns {string}
     */
    _labelForPhase(commission, _phase) {
        if (commission?.asset_type === 'miniature') {
            const canvasPx =
                { T: 400, S: 1200, M: 1200, L: 1600, H: 2000, G: 2400 }[
                    commission.miniature_size
                ] || 1200;
            return `Drop the miniature PSD (author it at ${canvasPx}×${canvasPx} — footprint + overflow ring) here or click to browse`;
        }
        if (commission?.asset_type === 'mapmaking-tile-brush') {
            return 'Drop a tile-brush PSD (named layers per part) here or click to browse';
        }
        return 'Drop image here or click to browse';
    }

    _renderPhase(phase) {
        const isActive = phase.status === 'in-progress' || phase.status === 'changes-requested';
        const isAccepted = phase.status === 'accepted';
        const isChangesRequested = phase.status === 'changes-requested';
        const cssClass = isActive
            ? 'active'
            : isAccepted
              ? 'accepted'
              : isChangesRequested
                ? 'changes-requested'
                : '';

        return html`
            <div class="phase-step ${cssClass}">
                <div class="phase-step-header">
                    <span class="phase-step-title">${phaseDisplayName(phase.phase_type)}</span>
                    ${this._phaseStatusBadge(phase.status)}
                </div>
                ${
                    phase.status === 'submitted'
                        ? html`
                    <div class="alert alert-info small mb-2 d-flex align-items-center gap-2">
                        <i class="bi bi-hourglass-split"></i>
                        Submitted for review. Your art director will review and provide feedback.
                    </div>
                `
                        : nothing
                }
                ${
                    isActive || phase.status === 'pending'
                        ? html`
                    <p class="text-muted small mb-2" style="line-height: 1.5;">${this._phaseDescription(phase.phase_type)}</p>
                `
                        : nothing
                }

                ${
                    isChangesRequested && phase.review_note
                        ? html`
                    <div class="review-note">
                        <strong>Changes requested:</strong> ${phase.review_note}
                    </div>
                `
                        : nothing
                }

                ${
                    isAccepted && phase.review_note
                        ? html`
                    <div class="text-muted small mt-1"><i class="bi bi-check-circle text-success"></i> ${phase.review_note}</div>
                `
                        : nothing
                }

                ${
                    phase.submissions && phase.submissions.length > 0
                        ? html`
                    <div class="submission-grid">
                        ${phase.submissions.map(
                            (s) => html`
                            <div class="submission-thumb ${phase.approved_submission_id === s.id ? 'approved' : ''}">
                                <media-thumb src="${this._renderedLayerUrl(s) || s.file_url}" type="${this._renderedLayerUrl(s) ? 'image/png' : s.mime_type || ''}" alt="${this._submissionLabel(this._selected, phase, s)}"
                                     @media-click="${() => this._handleSubmissionClick(s)}"></media-thumb>
                                ${this._renderSubmissionProcessing(s)}
                                <div class="submission-thumb-info d-flex justify-content-between align-items-center">
                                    <span>
                                        ${this._submissionLabel(this._selected, phase, s)}
                                        ${phase.approved_submission_id === s.id ? html` <i class="bi bi-check-circle-fill text-success"></i>` : nothing}
                                    </span>
                                    ${
                                        isActive
                                            ? html`
                                        <button class="btn btn-link btn-sm p-0 text-danger" style="font-size: 0.7rem; line-height: 1;"
                                            @click="${(e) => {
                                                e.stopPropagation();
                                                this._removeSubmission(phase.id, s.id);
                                            }}">
                                            <i class="bi bi-trash"></i>
                                        </button>
                                    `
                                            : nothing
                                    }
                                </div>
                            </div>
                        `,
                        )}
                    </div>
                    ${this._renderMiniatureGridPreview(phase)}
                `
                        : nothing
                }

                ${
                    isActive
                        ? html`
                    ${
                        (phase.submissions?.length || 0) <
                        this._maxSubmissionsForPhase(this._selected, phase)
                            ? html`
                        <upload-progress class="mt-2"
                            .api="${api}"
                            accept="${this._acceptForPhase(this._selected, phase)}"
                            label="${this._labelForPhase(this._selected, phase)}"
                            @upload-complete="${(e) => this._handlePhaseUploadComplete(phase.id, e)}">
                        </upload-progress>
                    `
                            : nothing
                    }
                    ${
                        this._selected?.asset_type === 'mapmaking-tile-brush' &&
                        this._tileBrushPreviewFor(phase)
                            ? html`
                        <div class="mt-2">
                            ${
                                this._tileBrushPreviewFor(phase).error
                                    ? html`<div class="alert alert-warning py-1 px-2 small mb-0">Preview unavailable: ${this._tileBrushPreviewFor(phase).error}</div>`
                                    : html`<tile-brush-previews
                                        .layers="${this._tileBrushPreviewFor(phase).layers}"></tile-brush-previews>`
                            }
                        </div>
                    `
                            : nothing
                    }
                    <div class="mt-2">
                        <textarea class="form-control form-control-sm mb-2" rows="2" placeholder="Add a comment for your art director (optional)"
                            .value="${this._submitNote}"
                            @input="${(e) => (this._submitNote = e.target.value)}"></textarea>
                        <button class="btn btn-primary btn-sm"
                            ?disabled="${this._submitting || !this._phaseReadyForReview(this._selected, phase)}"
                            @click="${() => this._submitForReview(phase.id)}">
                            ${this._submitting ? 'Submitting...' : 'Submit for Review'}
                        </button>
                        ${
                            this._submitting
                                ? nothing
                                : html`<span class="submit-blocker">${
                                      phaseSubmitBlocker(
                                          this._selected?.asset_type,
                                          phase.phase_type,
                                          phase.submissions,
                                      ) || ''
                                  }</span>`
                        }
                    </div>
                `
                        : nothing
                }
            </div>
        `;
    }

    _renderModal() {
        const c = this._selected;
        if (!c || !this._modal) return nothing;

        const isStyleGuide = this._modal === 'style-guide';
        const title = isStyleGuide ? 'Style Guide' : 'Technical Specification';
        const content = isStyleGuide ? c.style_guide : c.technical_spec;
        const filename = isStyleGuide
            ? `${c.asset_type}-style-guide.md`
            : `${c.asset_type}-technical-spec.md`;

        if (!content) return nothing;

        return html`
            <div class="modal-overlay" @click="${(e) => {
                if (e.target === e.currentTarget) this._modal = null;
            }}">
                <div class="modal-panel">
                    <div class="modal-header">
                        <h5>${title}</h5>
                        <button class="btn-close" @click="${() => (this._modal = null)}"></button>
                    </div>
                    <div class="modal-body">
                        <div class="spec-text">${content}</div>
                    </div>
                    <div class="modal-footer">
                        <button class="btn btn-outline-secondary btn-sm" @click="${() => downloadMarkdown(filename, content)}">
                            <i class="bi bi-download"></i> Download .md
                        </button>
                        <button class="btn btn-secondary btn-sm" @click="${() => (this._modal = null)}">Close</button>
                    </div>
                </div>
            </div>
        `;
    }

    render() {
        return html`
            ${this._error ? html`<div class="alert alert-danger">${this._error}</div>` : ''}
            ${this._view === 'list' ? this._renderList() : ''}
            ${this._view === 'detail' ? this._renderDetail() : ''}
            ${this._renderModal()}
        `;
    }
}

customElements.define('assignments-view', AssignmentsView);
